import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. The strobes, the lyres and the laser sheet
// are the only light there is, and a wash under them would show the hall
// between two events - which is the one thing this piece must never do.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'warehouse',
  family: 'stage',
  danceability: 0.90,
  energyLevel: 0.88,
  bpmMin: 120,
  bpmMax: 150,
  movement: 0.75,
  tonality: 0.35,
  prefers: { gridStrength: [0.70, 1.0], percussiveness: [0.50, 1.0] },
  requiresTier: 0,
  enabled: true
};

// Warehouse: an empty concrete hall in haze, revealed only where the light
// passes. Techno, hard house, minimal, electro.
//
// THE PHOTO IS THE SCENE, AND THE LIGHT IS COMPUTED ON IT. berlin.jpeg (an
// unlit hall, one-point perspective, pillars, trusses, a DJ platform at the
// far end) is a full-frame quad whose luminance is capped at 0.06 without
// light: between two events the hall is guessed at, not seen. Every source -
// strobes, lyre beams, the laser sheet, the platform's glow - is a term in
// the photo's fragment shader, summed per pixel and MULTIPLIED by the photo:
// the concrete takes the colour of what hits it and has none of its own.
//
// THE PHOTO HAS A DEPTH. tools/build-scenes.mjs runs tools/depth-scene.py
// (Depth Anything V2, the successor of MiDaS) and recalibrates the result
// on the floor, so the map is METRIC in hall units: the camera stands
// hc = 1 - vanish.y above the floor and the bottom of the frame is at depth
// 1. From the map every pixel has a 3D position P and a normal, and that is
// what makes the light REVEAL rather than tint: a strobe on a truss lights the
// pillar next to it and not the one at the far end (1 / (1 + k d^2) on the
// real distance), a beam makes a spot where its axis meets a surface, and the
// laser sheet is a PLANE through the platform - its trace is the pixels whose
// height lies on the plane, a hard line across every pillar and the floor.
//
// THE SAME CAMERA FOR THE 3D PARTS. Cones and dust are placed in the photo's
// own camera space (origin at the lens, +z into the hall, +y up) and projected
// by hand with the manifest's vanishing point and the quad's cover fit, so a
// cone lands on the photo exactly where its light does, and both are soft-
// depth-tested against the map. Nothing is in world space; scene-manager's
// camera drift is simply ignored, as it must be on a photograph.
//
// THE DARK IS THE POINT. A strobe is a lamp with StrobeWall's thermal
// inertia (45 ms up, 180 ms down), a bar flash is 60 ms, and the comfort
// rules are StrobeWall's: never more than three full-frame flashes a second,
// and a one-second mean luminance capped at 0.40 by a governor on the
// strobes' gain.

// --- the hall, in the manifest's units ----------------------------------------
// Fallback meta, identical to tools/build-scenes.mjs's; the manifest wins.
const FALLBACK_META = { vanish: { x: 0.50, y: 0.48 }, booth: { x: 0.50, y: 0.55 }, floorY: 0.72, sourceSize: [1376, 768] };
const SCENE_MANIFEST = 'textures/warehouse.json';
const W_MAX_DEFAULT = 1.25;
// Where the fixtures hang. The pillar rows stand at |x| = 0.80 (the front
// left pillar: u 0.21, base v 0.82 -> z 1.53, x -0.80) and their capitals
// carry the longitudinal beams at height 0.80 (the left beam leaves the frame
// at v 0.17). Strobes a little above the beam, lyres just under it.
const PILLAR_X = 0.80;
const BEAM_H = 0.80;
const STROBE_Z = [1.25, 1.25, 2.1, 2.1, 3.6, 3.6];
const STROBE_SIDE = [-1, 1, -1, 1, -1, 1];
const LYRE_Z = [1.05, 1.05, 1.6, 1.6, 2.5, 2.5, 4.0, 4.0];
const LYRE_SIDE = [-1, 1, -1, 1, -1, 1, -1, 1];
// The platform: booth.y is the floor at its foot (z 7.4), the deck sits 0.22
// above it, the laser leaves at chest height above the deck.
const BOOTH_Z = 7.4;
const BOOTH_H = 0.22;
const SHEET_H = 0.50;

// --- the photo ---------------------------------------------------------------
// The ceiling without light, as the brief states it: 0.06 of luminance. "N%"
// is a percentage of the OUTPUT, not of linear light (ACES then sRGB), so the
// linear gain is set so that the photo's p99 lands on 0.06 x 255 = 15 on the
// screen - calibrated in the harness, see AMBIENT_LINEAR.
const AMBIENT_OUT = 0.06;
// Measured in the harness: the photo's p99 pixel lands on 15/255 with this
// linear gain on the p99 of the photo (2/255 at 0.0115: the sRGB inverse,
// which the chain's toe crushes - see StrobeWall's AMB_LIP for the same lesson).
const AMBIENT_LINEAR = 0.045;

// --- the sources -------------------------------------------------------------
const STROBE_COLOR = [0.86, 0.92, 1.0];   // cold white
const STROBE_GAIN = 2.6;
const STROBE_K = 2.2;             // 1 / (1 + k d^2), d in hall units: the far end gets a tenth
const STROBE_HALO = 0.22;         // the lamp's own bloom in the haze, screen space
const STROBE_FOG = 0.030;         // the haze lit between the lens and the surface
const HEAT_RATE = 22.0;           // StrobeWall's filament: ~45 ms up
const COOL_RATE = 5.5;            // ~180 ms down
const BEAT_MS = [0.050, 0.110];   // bands.high: the brighter the top, the shorter the hit
const BAR_MS = 0.060;             // every strobe together, once a bar
const DROP_BLACK_BEATS = 1.0;
const DROP_BEATS = 8.0;           // converge + sheet on the floor, after the black

const BEAM_GAIN = 1.1;
const BEAM_R0 = 0.035;            // cone radius at the lens, hall units
const BEAM_TAN = 0.075;           // and its growth per unit of length (~4.3 deg half-angle)
const BEAM_LENGTH = 7.0;
const BEAM_SAT = 0.85;            // the brief: palette colours at saturation 0.85
const BEAM_LIGHT = 0.55;
// The lyres PULSE on the beat (BeamWall's own pulse): the beam is at its
// brightest on the hit and falls to a floor before the next one, which is
// what keeps the dark between two events. Without it the cones were a steady
// 0.06 of mean luminance on their own.
const BEAM_PULSE_FLOOR = 0.30;
const BEAM_PULSE_DECAY = 9.0;
const MOVE_MS = 110;              // BeamWall's notch: a lyre moves in 110 ms, then holds
const CONE_GAIN = 0.20;
const CONE_FADE = 0.22;           // per unit of length, in the haze

const SHEET_GAIN = 7.0;
const SHEET_THICKNESS = 0.016;    // hall units; never under 2 screen pixels
const SHEET_FAN_RAYS = 9;
const SHEET_SPREAD = 0.70;        // lateral half-extent per unit of distance from the emitter
const SHEET_MODE_SWEEP = 0, SHEET_MODE_STATIC = 1, SHEET_MODE_FAN = 2;
const SHEET_MODES = [SHEET_MODE_SWEEP, SHEET_MODE_STATIC, SHEET_MODE_FAN];
const SHEET_SWEEP = [-0.015, 0.10];   // tan(pitch), + = down toward the lens
const SHEET_STATIC = 0.012;
const SHEET_DROP_H = 0.045;       // "au ras du sol"

const BOOTH_GAIN = 0.35;
const BOOTH_HALO = 0.10;
const BOOTH_LOW = 0.6;            // bands.low swings the glow by this much

const FOG_SIGMA = 0.22;           // haze density per unit of depth

// --- dust ----------------------------------------------------------------------
const DUST_BOX = { x: 1.55, h: 1.25, z0: 0.75, z1: 6.5 };
const DUST_SIZE_PX = 2.4;         // at depth 1, on a 720-line buffer
const DUST_GAIN = 0.9;
const DUST_STROBE = 0.35;

// --- the rhythm ----------------------------------------------------------------
const STROBE_PATTERNS = ['tournant', 'alterne', 'croise', 'aleatoire'];
const PAT_FAN = 0, PAT_WAVE = 1, PAT_CROSS = 2, PAT_RAIN = 3, PAT_CHASE = 4, PAT_CONVERGE = 5;
const PATTERN_CYCLE = [PAT_FAN, PAT_WAVE, PAT_CROSS, PAT_RAIN, PAT_CHASE];
const FALLBACK_BPM = 128;

// --- comfort. NOT aesthetic settings -----------------------------------------
const FULL_PER_SEC = 3;           // never more than three full-frame flashes a second
const LUM_LIMIT = 0.40;           // rolling one-second mean of the frame
const LUM_WINDOW = 1.0;
// The predicted frame luminance, from state this file owns (the strobes'
// mean temperature) through a transfer measured in the harness: the dark
// frame, and the frame with every strobe at full temperature.
const LUM_DARK = 0.012;
const LUM_FULL = 0.49;
const LUM_POW = 0.85;
const GAIN_RELEASE = 0.6;         // per second, back up slowly

// THE LIGHT IS COMPUTED AT A FRACTION OF THE SCREEN. Measured on the Pi at
// 1280x400, the per-pixel sum of the sources at full resolution cost 29 ms
// at tier 0 and 66 at tier 2 - the evaluation itself, not the texture taps
// (precomputing the normals saved 3 ms). The sources vary smoothly over the
// frame, so strobes, beams, cones and dust are drawn into two targets at
// `light` of the screen (the lit surfaces, and the haze) and the display
// pass, at full resolution, keeps only what must stay sharp: the photo, the
// laser sheet's line, and the dither.
const TIER = {
  0: { strobes: 4, lyres: 0, sheet: true, dust: 0, light: 0.35 },
  1: { strobes: 4, lyres: 4, sheet: true, dust: 1200, light: 0.40 },
  2: { strobes: 6, lyres: 8, sheet: true, dust: 2500, light: 0.50 }
};
const LIGHT_RANGE = 4.0;          // the light target holds light / LIGHT_RANGE
const HAZE_RANGE = 2.0;
const DEFAULT_TIER = 2;
const SAFE_FLOOR = 0.50;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// =============================================================================
// SHADERS
// =============================================================================

// The photo's camera, shared by every shader here.
//   uProj = (vanish.x, vanish.y, photo aspect, hc)   hc = 1 - vanish.y
//   uFit  = (sx, sy): the share of the photo the frame shows (cover fit)
// Image coordinates are (u, v), v DOWN, as the manifest measures them.
const CAMERA_CHUNK = `
  uniform vec4 uProj;
  uniform vec2 uFit;
  vec2 imageOf(vec3 P) { return vec2(uProj.x + P.x / (uProj.z * P.z), uProj.y - P.y / P.z); }
  vec2 ndcOf(vec2 img) { return vec2((img.x - 0.5) * 2.0 / uFit.x, (0.5 - img.y) * 2.0 / uFit.y); }
  vec2 imageOfNdc(vec2 ndc) { return vec2(0.5 + ndc.x * 0.5 * uFit.x, 0.5 - ndc.y * 0.5 * uFit.y); }
  vec3 pointOf(vec2 img, float w) {
    float z = 1.0 / max(w, 0.02);
    return vec3((img.x - uProj.x) * uProj.z * z, (uProj.y - img.y) * z, z);
  }
`;
// The depth map: proximity w = 1 / z, 16 bits split over R (high) and G (low).
const DEPTH_CHUNK = `
  uniform sampler2D uDepth;
  uniform float uWMax;
  float depthAt(vec2 img) {
    vec2 rg = texture2D(uDepth, vec2(img.x, 1.0 - img.y)).rg;
    return (256.0 * rg.x + rg.y) * (uWMax / 257.0);
  }
`;

const photoVertexShader = `
  precision mediump float;
  varying vec2 vNdc;
  void main() {
    vNdc = position.xy * 2.0;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// PASS 1, reduced resolution: the light on the surfaces (RGB, over
// LIGHT_RANGE) and the strobes' haze (A, over HAZE_RANGE).
function lightFragmentShader(ns, nb) {
  return `
  precision mediump float;
  ${CAMERA_CHUNK}
  ${DEPTH_CHUNK}
  uniform sampler2D uNormal;
  uniform vec4 uStrobe[${Math.max(1, ns)}];     // xyz, level
  uniform vec2 uStrobeImg[${Math.max(1, ns)}];  // its image position, aspect-scaled
  uniform vec4 uBeamO[${Math.max(1, nb)}];      // origin, level
  uniform vec4 uBeamD[${Math.max(1, nb)}];      // direction, tan(half-angle)
  uniform vec3 uBeamC[${Math.max(1, nb)}];
  uniform vec4 uBooth;          // xyz, level
  uniform vec3 uBoothC;
  varying vec2 vNdc;

  void main() {
    vec2 img = imageOfNdc(vNdc);
    vec2 tc = vec2(img.x, 1.0 - img.y);
    float w = depthAt(img);
    vec3 P = pointOf(img, w);
    vec3 N = texture2D(uNormal, tc).xyz * 2.0 - 1.0;
    vec3 light = vec3(0.0);
    float haze = 0.0;
    float fogHere = 1.0 - exp(-${FOG_SIGMA.toFixed(3)} * P.z);
    vec2 imgA = img * vec2(uProj.z, 1.0);

    // STROBES: a radial light from the lamp's 3D position, shaded by the
    // surface, plus the lamp's own halo and the haze it lights.
    for (int i = 0; i < ${ns}; i++) {
      vec4 s = uStrobe[i];
      vec3 d = P - s.xyz;
      float d2 = dot(d, d);
      vec3 L = d * inversesqrt(max(d2, 1e-4));
      float lam = 0.30 + 0.70 * max(0.0, dot(N, -L));
      float fall = 1.0 / (1.0 + d2 * ${STROBE_K.toFixed(2)});
      light += (s.w * fall * lam * ${STROBE_GAIN.toFixed(2)}) * vec3(1.0);
      vec2 dd = imgA - uStrobeImg[i];
      haze += s.w * (${STROBE_HALO.toFixed(3)} * exp(-dot(dd, dd) * 60.0) + ${STROBE_FOG.toFixed(3)} * fogHere * fall * 3.0);
    }

    // BEAMS: a spot where the axis meets a surface.
    for (int i = 0; i < ${nb}; i++) {
      vec4 o = uBeamO[i];
      vec4 dir = uBeamD[i];
      vec3 v = P - o.xyz;
      float t = max(dot(v, dir.xyz), 0.05);
      vec3 perp = v - dir.xyz * t;
      float r = length(perp);
      float R = ${BEAM_R0.toFixed(3)} + t * dir.w;
      float spot = 1.0 - smoothstep(R * 0.55, R, r);
      float att = 1.0 / (1.0 + t * t * 0.22);
      float face = 0.40 + 0.60 * max(0.0, dot(N, -dir.xyz));
      light += uBeamC[i] * (o.w * spot * att * face * ${BEAM_GAIN.toFixed(2)});
    }

    // THE PLATFORM's light on the surfaces around it.
    {
      vec3 d = P - uBooth.xyz;
      light += uBoothC * (uBooth.w * ${BOOTH_GAIN.toFixed(2)} / (1.0 + dot(d, d) * 2.5));
    }
    gl_FragColor = vec4(light / ${LIGHT_RANGE.toFixed(1)}, haze / ${HAZE_RANGE.toFixed(1)});
  }
`;
}

// PASS 2, full resolution: the photo times (ambient + light), the haze, the
// laser sheet's line, the platform's halo, the safe zone, the dither.
const displayFragmentShader = `
  precision mediump float;
  ${CAMERA_CHUNK}
  ${DEPTH_CHUNK}
  uniform sampler2D uMap;
  uniform sampler2D uLight;
  uniform sampler2D uHaze;
  uniform float uAmbient;
  uniform float uPixelH;        // image-height units per screen pixel
  uniform float uTime;
  uniform float uDebug;         // 1: the constants, 2: the depth map
  uniform vec4 uMeta;           // vanish.xy, booth.xy
  uniform float uFloorY;
  uniform vec3 uStrobeC;
  uniform vec4 uSheet0;         // emitter xyz, level
  uniform vec4 uSheet1;         // tan(pitch), thickness, fan (0/1), spread
  uniform vec3 uSheetC;
  uniform vec4 uBooth;          // xyz, level
  uniform vec3 uBoothC;
  uniform vec2 uBoothImg;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vNdc;

  float safeAt(vec2 ndc) {
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(0.32, 1.0, smoothstep(0.0, uSafeFeather, d));
    return ${SAFE_FLOOR.toFixed(2)} + (att - 0.32) * (${((1 - SAFE_FLOOR) / 0.68).toFixed(5)});
  }
  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  void main() {
    vec2 img = imageOfNdc(vNdc);
    vec2 tc = vec2(img.x, 1.0 - img.y);
    vec2 sc = vNdc * 0.5 + 0.5;
    vec3 ph = texture2D(uMap, tc).rgb;
    vec4 lt = texture2D(uLight, sc);
    vec3 light = lt.rgb * ${LIGHT_RANGE.toFixed(1)};
    vec3 haze = uStrobeC * (lt.a * ${HAZE_RANGE.toFixed(1)}) + texture2D(uHaze, sc).rgb;
    float w = depthAt(img);
    vec3 P = pointOf(img, w);

    // THE LASER SHEET: a plane through the emitter, pitched about x. The
    // trace is where the pixel's height sits on the plane - the floor is cut
    // along a horizontal line, each pillar at the plane's height there. At
    // full resolution: this is the one hard line of the piece.
    {
      vec4 e = uSheet0;
      float dz = e.z - P.z;
      if (e.w > 0.002 && dz > 0.15) {
        float hP = P.y + uProj.w;
        float hS = (e.y + uProj.w) - dz * uSheet1.x;
        float dy = hP - hS;
        float thk = max(uSheet1.y, 2.0 * uPixelH * P.z);
        float line = exp(-(dy * dy) / (thk * thk));
        float lat = 1.0 - smoothstep(0.80, 1.0, abs(P.x - e.x) / (dz * uSheet1.w + 0.05));
        if (uSheet1.z > 0.5) {
          float a = (P.x - e.x) / dz;
          line *= pow(0.5 + 0.5 * cos(a * ${(2 * Math.PI * SHEET_FAN_RAYS).toFixed(3)} / (2.0 * uSheet1.w)), 14.0);
        }
        light += uSheetC * (e.w * line * lat * ${SHEET_GAIN.toFixed(2)});
      }
    }
    // THE PLATFORM's halo in the haze.
    {
      vec2 dd = img * vec2(uProj.z, 1.0) - uBoothImg;
      haze += uBoothC * uBooth.w * ${BOOTH_HALO.toFixed(2)} * exp(-dot(dd, dd) * 45.0);
    }

    // THE CONCRETE TAKES THE LIGHT'S COLOUR, never a tint of its own.
    vec3 col = ph * (uAmbient + light) + haze;

    if (uDebug > 1.5) col = vec3(w / uWMax);
    if (uDebug > 0.5 && uDebug < 1.5) {
      vec2 px = img / uPixelH;
      vec2 cv = abs(px - uMeta.xy / uPixelH), cb = abs(px - uMeta.zw / uPixelH);
      if ((cv.x < 1.0 && cv.y < 18.0) || (cv.y < 1.0 && cv.x < 18.0)) col = vec3(1.0, 0.1, 0.1);
      if ((cb.x < 1.0 && cb.y < 18.0) || (cb.y < 1.0 && cb.x < 18.0)) col = vec3(0.1, 1.0, 0.2);
      if (abs(img.y - uFloorY) / uPixelH < 0.7) col = vec3(0.2, 0.5, 1.0);
      col = mix(col, ph, 0.35);
    }

    col *= safeAt(vNdc);
    vec2 fcw = mod(floor(gl_FragCoord.xy), 64.0);
    col *= 1.0 + (hash12(fcw + fract(uTime * 3.0) * 7.0) - 0.5) * 0.03;
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

// A cone in the haze. The geometry is a unit cylinder (radius 1, y in 0..1)
// that the vertex shader stretches along the beam. Its brightness is the
// chord through the volume - brightest where the surface faces the lens
// (the centre of the tube), nothing at the silhouette - faded along the
// length, and soft-depth-tested against the photo so a pillar in front of it
// cuts it. The test reads the depth map at gl_FragCoord: a screen-linear
// coordinate passed as a varying under w = z would be interpolated
// perspective-correctly, i.e. wrongly, across the beam's long triangles.
const coneVertexShader = `
  precision mediump float;
  ${CAMERA_CHUNK}
  uniform vec3 uOrigin;
  uniform vec3 uDir;
  uniform float uTan;
  uniform float uLength;
  varying vec3 vN;
  varying vec3 vView;
  varying float vAlong;
  varying float vZ;
  void main() {
    vec3 up = abs(uDir.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 sx = normalize(cross(up, uDir));
    // (sx, uDir, sy) right-handed like the cylinder's own (x, y, z): the
    // other way round the winding flips and FrontSide culls the whole beam.
    vec3 sy = cross(sx, uDir);
    float along = position.y;
    float r = ${BEAM_R0.toFixed(3)} + along * uLength * uTan;
    vec3 P = uOrigin + uDir * (along * uLength) + (sx * position.x + sy * position.z) * r;
    vN = normalize(sx * position.x + sy * position.z - uDir * uTan);
    vView = normalize(P);
    vAlong = along;
    vZ = P.z;
    vec2 ndc = ndcOf(imageOf(P));
    gl_Position = vec4(ndc * P.z, 0.0, P.z);
  }
`;
const coneFragmentShader = `
  precision mediump float;
  ${CAMERA_CHUNK}
  ${DEPTH_CHUNK}
  uniform vec3 uColor;
  uniform float uLevel;
  uniform float uLength;
  uniform vec2 uResolution;
  varying vec3 vN;
  varying vec3 vView;
  varying float vAlong;
  varying float vZ;
  void main() {
    float facing = abs(dot(normalize(vN), vView));
    float chord = pow(facing, 0.75);
    float len = exp(-vAlong * uLength * ${CONE_FADE.toFixed(3)}) * smoothstep(0.0, 0.06, vAlong);
    vec2 ndc = gl_FragCoord.xy / uResolution * 2.0 - 1.0;   // uResolution: the haze target's
    float wScene = depthAt(imageOfNdc(ndc));
    float behind = smoothstep(0.0, 0.03, wScene - 1.0 / max(vZ, 0.05));
    float a = chord * len * uLevel * (1.0 - behind) * ${CONE_GAIN.toFixed(3)};
    gl_FragColor = vec4(uColor * a, 1.0);
  }
`;

function dustVertexShader(ns, nb) {
  return `
  precision mediump float;
  ${CAMERA_CHUNK}
  ${DEPTH_CHUNK}
  attribute vec3 aSeed;
  uniform float uTime;
  uniform float uPixelScale;
  uniform vec4 uStrobe[${Math.max(1, ns)}];
  uniform vec4 uBeamO[${Math.max(1, nb)}];
  uniform vec4 uBeamD[${Math.max(1, nb)}];
  uniform vec3 uBeamC[${Math.max(1, nb)}];
  uniform vec4 uSheet0;
  uniform vec4 uSheet1;
  uniform vec3 uSheetC;
  uniform vec3 uStrobeC;
  varying vec3 vCol;
  varying float vA;
  void main() {
    vec3 P = position;
    P.x += sin(uTime * 0.23 + aSeed.x * 6.283) * 0.05;
    P.y += sin(uTime * 0.17 + aSeed.y * 6.283) * 0.03;
    P.z += cos(uTime * 0.19 + aSeed.z * 6.283) * 0.05;
    vec3 col = vec3(0.0);
    float a = 0.0;
    for (int i = 0; i < ${nb}; i++) {
      vec4 o = uBeamO[i];
      if (o.w < 0.002) continue;
      vec4 d = uBeamD[i];
      vec3 v = P - o.xyz;
      float t = dot(v, d.xyz);
      if (t < 0.0 || t > ${BEAM_LENGTH.toFixed(1)}) continue;
      float r = length(v - d.xyz * t);
      float R = ${BEAM_R0.toFixed(3)} + t * d.w;
      float rv = (1.0 - smoothstep(R * 0.4, R, r)) * o.w;
      col += uBeamC[i] * rv; a += rv;
    }
    for (int i = 0; i < ${ns}; i++) {
      vec4 s = uStrobe[i];
      if (s.w < 0.002) continue;
      vec3 d = P - s.xyz;
      float rv = s.w * ${DUST_STROBE.toFixed(2)} / (1.0 + dot(d, d) * 1.5);
      col += uStrobeC * rv; a += rv;
    }
    {
      vec4 e = uSheet0;
      float dz = e.z - P.z;
      if (e.w > 0.002 && dz > 0.15) {
        float hS = (e.y + uProj.w) - dz * uSheet1.x;
        float dy = (P.y + uProj.w) - hS;
        float rv = e.w * exp(-(dy * dy) / (uSheet1.y * uSheet1.y * 9.0)) * 0.6;
        col += uSheetC * rv; a += rv;
      }
    }
    vec2 img = imageOf(P);
    float wScene = depthAt(img);
    float behind = smoothstep(0.0, 0.03, wScene - 1.0 / P.z);
    vA = min(a, 1.5) * (1.0 - behind) * ${DUST_GAIN.toFixed(2)} * (0.5 + 0.5 * aSeed.y);
    vCol = a > 0.001 ? col / a : vec3(0.0);
    vec2 ndc = ndcOf(img);
    gl_Position = vec4(ndc * P.z, 0.0, P.z);
    gl_PointSize = clamp(${DUST_SIZE_PX.toFixed(1)} * uPixelScale / P.z * (0.7 + 0.6 * aSeed.x), 1.0, 5.0);
  }
`;
}
const dustFragmentShader = `
  precision mediump float;
  varying vec3 vCol;
  varying float vA;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float r2 = dot(c, c);
    if (r2 > 0.25) discard;
    float k = exp(-r2 * 12.0);
    gl_FragColor = vec4(vCol * (vA * k), 1.0);
  }
`;

// =============================================================================

export class WarehouseVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    this._debug = urlParam('wareconst') === '1' ? 1 : (urlParam('waredepth') === '1' ? 2 : 0);
    const amb = parseFloat(urlParam('wareambient'));
    this._forcedAmbient = Number.isFinite(amb) ? amb : null;
    // ?warestrobe=all holds every strobe lit, ?warestrobe=N one lamp, ?warenone=1
    // nothing but the platform: the calibration frames.
    this._forceStrobe = urlParam('warestrobe');
    this._forceNone = urlParam('warenone') === '1';
    this._forceDrop = urlParam('waredrop') === '1';
    const sm = urlParam('waresheet');
    this._forcedSheet = sm != null && SHEET_MODES[+sm] != null ? +sm : null;
    const bp = urlParam('warebeams');
    this._forcedPattern = bp != null && Number.isFinite(+bp) ? +bp : null;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._time = 0;
    this._paused = false;
    this._meta = FALLBACK_META;
    this._aspectPhoto = FALLBACK_META.sourceSize[0] / FALLBACK_META.sourceSize[1];
    this._ready = false;
    this._pixelH = 1 / 720;
    this._pixelScale = 720 / 720;

    const ns = this._layout.strobes, nb = this._layout.lyres;
    this._strobeTemp = new Float32Array(ns);
    this._strobeUntil = new Float32Array(ns).fill(-1);
    this._strobePos = [];
    for (let i = 0; i < ns; i++) this._strobePos.push(new THREE.Vector3(STROBE_SIDE[i] * (PILLAR_X - 0.02), BEAM_H + 0.06 - (1 - FALLBACK_META.vanish.y), STROBE_Z[i]));
    this._lyrePos = [];
    this._lyreDir = [];
    this._lyreFrom = [];
    this._lyreTo = [];
    this._lyreLevel = new Float32Array(nb);
    for (let i = 0; i < nb; i++) {
      this._lyrePos.push(new THREE.Vector3(LYRE_SIDE[i] * (PILLAR_X - 0.08), BEAM_H - 0.02 - (1 - FALLBACK_META.vanish.y), LYRE_Z[i]));
      this._lyreDir.push(new THREE.Vector3(0, -1, 0));
      this._lyreFrom.push(new THREE.Vector3(0, -1, 0));
      this._lyreTo.push(new THREE.Vector3(0, -1, 0));
      this._lyreLevel[i] = 1;
    }
    this._sinceStep = 10;
    this._pattern = PAT_FAN;
    this._patternIdx = 0;
    this._step = 0;
    this._strobePattern = 0;
    this._strobeTurn = 0;
    this._sheetMode = SHEET_MODE_SWEEP;
    this._sheetIdx = 0;
    this._sheetPitch = 0.03;
    this._lastPhrase = null;
    this._lastBeat = null;
    this._lastBar = null;
    this._ownBeat = 0;
    this._beatsSeen = 0;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._dropAt = -1e9;
    this._lastDropAt = -1e9;
    this._fullFires = [];
    this._lumSamples = [];
    this._lumMean = 0;
    this._gain = 1;
    this._lowSmooth = 0.5;
    this._highSmooth = 0.5;
    this._boothLevel = 0.6;
    this._lastPaletteRef = undefined;
    this._beamColors = [new THREE.Color(0x3060ff), new THREE.Color(0xff2060)];
    this._sheetColor = new THREE.Color(0x20ff80);
    this._boothColor = new THREE.Color(0xff6a2a);
    this._eventsPerBeat = [];
    this._skippedBars = 0;

    this._buildPhoto();
    this._buildCones();
    this._buildDust();
    this._loadScene();
  }

  // --- the scene, from the manifest ------------------------------------------
  _loadScene() {
    const self = this;
    const want = this._tier >= 2 ? 2560 : 1600;
    fetch(SCENE_MANIFEST)
      .then(function (r) { if (!r.ok) throw new Error('no manifest'); return r.json(); })
      .then(function (m) {
        if (!self._scene) return null;
        if (m.vanish && m.booth) {
          self._meta = { vanish: m.vanish, booth: m.booth, floorY: m.floorY != null ? m.floorY : FALLBACK_META.floorY, sourceSize: m.sourceSize || FALLBACK_META.sourceSize };
        }
        self._aspectPhoto = self._meta.sourceSize[0] / self._meta.sourceSize[1];
        self._p99 = m.p99Luma || 0.5;
        const photo = (m.variants || []).filter(function (v) { return !v.role; })
          .sort(function (a, b) { return Math.abs(a.width - want) - Math.abs(b.width - want); })[0];
        const depth = (m.variants || []).filter(function (v) { return v.role === 'depth'; })[0];
        const normal = (m.variants || []).filter(function (v) { return v.role === 'normal'; })[0];
        if (!photo) throw new Error('no photo variant');
        if (!depth || !normal) throw new Error('no depth or normal map (run tools/build-scenes.mjs with the social python)');
        self._wMax = depth.wMax || W_MAX_DEFAULT;
        const load = function (f) { return new Promise(function (res, rej) { new THREE.TextureLoader().load('textures/' + f, res, undefined, rej); }); };
        return Promise.all([load(photo.file), load(depth.file), load(normal.file)]);
      })
      .then(function (pair) {
        if (!pair || !self._scene) return;
        const tex = pair[0], dep = pair[1], nor = pair[2];
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.minFilter = THREE.LinearMipmapLinearFilter;
        tex.anisotropy = self._tier >= 2 ? 4 : 1;   // anisotropy is paid per tap on the Pi
        // The depth: linear filtering is exact on the two-byte split (the
        // interpolation is linear in both bytes), no mipmaps, no colour space.
        // The normal map the same, without the split.
        for (const t of [dep, nor]) {
          t.colorSpace = THREE.NoColorSpace;
          t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
          t.minFilter = THREE.LinearFilter;
          t.magFilter = THREE.LinearFilter;
          t.generateMipmaps = false;
        }
        self._photoTex = tex;
        self._depthTex = dep;
        self._normalTex = nor;
        const shared = self._sharedUniforms;
        shared.uDepth.value = dep;
        self._lightUniforms.uNormal.value = nor;
        shared.uWMax.value = self._wMax;
        shared.uProj.value.set(self._meta.vanish.x, self._meta.vanish.y, self._aspectPhoto, 1 - self._meta.vanish.y);
        const u = self._photoUniforms;
        u.uMap.value = tex;
        u.uMeta.value.set(self._meta.vanish.x, self._meta.vanish.y, self._meta.booth.x, self._meta.booth.y);
        u.uFloorY.value = self._meta.floorY;
        u.uAmbient.value = self._forcedAmbient != null ? self._forcedAmbient : AMBIENT_LINEAR / Math.max(0.05, self._p99);
        self._placeFixtures();
        self._photo.visible = true;
        for (const c of self._cones) c.mesh.visible = true;
        if (self._dust) self._dust.visible = true;
        self._ready = true;
      })
      .catch(function (err) {
        if (!self._scene) return;
        console.warn('[warehouse] no hall photo (' + (err && err.message) + '): nothing to light - run tools/build-scenes.mjs');
      });
  }

  // Everything hangs from the manifest's horizon: the camera height is
  // 1 - vanish.y, and the fixtures' y are heights above the FLOOR.
  _placeFixtures() {
    const hc = 1 - this._meta.vanish.y;
    for (let i = 0; i < this._strobePos.length; i++) this._strobePos[i].set(STROBE_SIDE[i] * (PILLAR_X - 0.02), BEAM_H + 0.06 - hc, STROBE_Z[i]);
    for (let i = 0; i < this._lyrePos.length; i++) this._lyrePos[i].set(LYRE_SIDE[i] * (PILLAR_X - 0.08), BEAM_H - 0.02 - hc, LYRE_Z[i]);
    this._boothPos = new THREE.Vector3((this._meta.booth.x - this._meta.vanish.x) * this._aspectPhoto * BOOTH_Z, BOOTH_H + 0.10 - hc, BOOTH_Z);
    this._sheetPos = new THREE.Vector3(this._boothPos.x, BOOTH_H + SHEET_H - hc, BOOTH_Z - 0.3);
  }

  _buildPhoto() {
    const ns = this._layout.strobes, nb = this._layout.lyres;
    this._sharedUniforms = {
      uProj: { value: new THREE.Vector4(FALLBACK_META.vanish.x, FALLBACK_META.vanish.y, this._aspectPhoto, 1 - FALLBACK_META.vanish.y) },
      uFit: { value: new THREE.Vector2(1, 1) },
      uDepth: { value: null },
      uWMax: { value: W_MAX_DEFAULT },
      uTime: { value: 0 },
      uStrobeC: { value: new THREE.Vector3(STROBE_COLOR[0], STROBE_COLOR[1], STROBE_COLOR[2]) },
      uStrobe: { value: Array.from({ length: Math.max(1, ns) }, () => new THREE.Vector4(0, 0, 1, 0)) },
      uStrobeImg: { value: Array.from({ length: Math.max(1, ns) }, () => new THREE.Vector2(0, 0)) },
      uBeamO: { value: Array.from({ length: Math.max(1, nb) }, () => new THREE.Vector4(0, 0, 1, 0)) },
      uBeamD: { value: Array.from({ length: Math.max(1, nb) }, () => new THREE.Vector4(0, -1, 0, BEAM_TAN)) },
      uBeamC: { value: Array.from({ length: Math.max(1, nb) }, () => new THREE.Vector3(1, 1, 1)) },
      uSheet0: { value: new THREE.Vector4(0, 0, BOOTH_Z, 0) },
      uSheet1: { value: new THREE.Vector4(0.03, SHEET_THICKNESS, 0, SHEET_SPREAD) },
      uSheetC: { value: new THREE.Vector3(0.2, 1, 0.5) },
      uBooth: { value: new THREE.Vector4(0, -0.2, BOOTH_Z, 0.6) },
      uBoothC: { value: new THREE.Vector3(1, 0.4, 0.2) },
      uBoothImg: { value: new THREE.Vector2(0.9, 0.55) }
    };
    this._resolution = { value: new THREE.Vector2(640, 360) };

    // The two reduced targets: the light on the surfaces, and the haze.
    const rtOpts = { type: THREE.UnsignedByteType, format: THREE.RGBAFormat, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false };
    this._rtLight = new THREE.WebGLRenderTarget(4, 4, rtOpts);
    this._rtHaze = new THREE.WebGLRenderTarget(4, 4, rtOpts);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;

    this._lightUniforms = Object.assign({}, this._sharedUniforms, { uNormal: { value: null } });
    this._lightScene = new THREE.Scene();
    this._lightQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._lightUniforms, vertexShader: photoVertexShader, fragmentShader: lightFragmentShader(ns, nb),
      depthTest: false, depthWrite: false
    }));
    this._lightQuad.frustumCulled = false;
    this._lightScene.add(this._lightQuad);
    this._hazeScene = new THREE.Scene();

    this._photoUniforms = Object.assign({}, this._sharedUniforms, {
      uMap: { value: null },
      uLight: { value: this._rtLight.texture },
      uHaze: { value: this._rtHaze.texture },
      uAmbient: { value: AMBIENT_LINEAR / 0.5 },
      uPixelH: { value: 1 / 720 },
      uDebug: { value: this._debug },
      uMeta: { value: new THREE.Vector4(0.5, 0.48, 0.5, 0.55) },
      uFloorY: { value: 0.72 },
      uSafeAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather
    });
    const mat = new THREE.ShaderMaterial({
      uniforms: this._photoUniforms, vertexShader: photoVertexShader, fragmentShader: displayFragmentShader,
      depthTest: false, depthWrite: false
    });
    this._photo = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    this._photo.frustumCulled = false;
    this._photo.renderOrder = -1000;
    this._photo.visible = false;
    const self = this;
    // THE TWO PASSES RUN HERE, just before the display quad draws: the
    // targets are refreshed from the same uniforms the frame uses.
    this._photo.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(_size);
      self._fit(_size.x, _size.y);
      const prev = renderer.getRenderTarget();
      renderer.setRenderTarget(self._rtLight);
      renderer.render(self._lightScene, self._ortho);
      renderer.setRenderTarget(self._rtHaze);
      renderer.setClearColor(0x000000, 1);
      renderer.clear(true, false, false);
      renderer.render(self._hazeScene, self._ortho);
      renderer.setRenderTarget(prev);
    };
    this._scene.add(this._photo);
  }

  // The cover fit: the frame shows all of the photo's width on a frame wider
  // than it (the kiosk's 3.2:1 shows rows 0.22 to 0.78), all of its height on
  // a narrower one.
  _fit(wpx, hpx) {
    const lw = Math.max(8, Math.round(wpx * this._layout.light)), lh = Math.max(8, Math.round(hpx * this._layout.light));
    if (this._rtLight.width !== lw || this._rtLight.height !== lh) { this._rtLight.setSize(lw, lh); this._rtHaze.setSize(lw, lh); }
    this._resolution.value.set(lw, lh);
    if (this._dustUniforms) this._dustUniforms.uPixelScale.value = lh / 720;
    const af = wpx / Math.max(1, hpx), ap = this._aspectPhoto;
    const fit = this._sharedUniforms.uFit.value;
    if (af >= ap) fit.set(1, ap / af); else fit.set(af / ap, 1);
    this._pixelH = fit.y / Math.max(1, hpx);
    this._photoUniforms.uPixelH.value = this._pixelH;
  }

  _buildCones() {
    this._cones = [];
    if (!this._layout.lyres) return;
    const geo = new THREE.CylinderGeometry(1, 1, 1, 18, 1, true);
    geo.translate(0, 0.5, 0);
    this._coneGeo = geo;
    for (let i = 0; i < this._layout.lyres; i++) {
      const uniforms = Object.assign({}, this._sharedUniforms, {
        uOrigin: { value: this._lyrePos[i] },
        uDir: { value: this._lyreDir[i] },
        uTan: { value: BEAM_TAN },
        uLength: { value: BEAM_LENGTH },
        uColor: { value: new THREE.Vector3(1, 1, 1) },
        uLevel: { value: 0 },
        uResolution: this._resolution
      });
      const mat = new THREE.ShaderMaterial({
        uniforms: uniforms, vertexShader: coneVertexShader, fragmentShader: coneFragmentShader,
        // Front faces only: the chord profile already peaks at the tube's
        // centre, and the back faces doubled the fill for the same look.
        transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.FrontSide
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.frustumCulled = false;
      mesh.visible = false;
      this._hazeScene.add(mesh);
      this._cones.push({ mesh, mat, uniforms });
    }
  }

  _buildDust() {
    this._dust = null;
    const n = this._layout.dust;
    if (!n) return;
    const pos = new Float32Array(n * 3), seed = new Float32Array(n * 3);
    const hc = 1 - FALLBACK_META.vanish.y;
    for (let i = 0; i < n; i++) {
      // uniform in the hall's VOLUME: z drawn so the count per unit of depth
      // is even (a point per unit of z, not per unit of 1/z)
      const z = lerp(DUST_BOX.z0, DUST_BOX.z1, Math.random());
      pos[i * 3] = (Math.random() * 2 - 1) * DUST_BOX.x;
      pos[i * 3 + 1] = Math.random() * DUST_BOX.h - hc;
      pos[i * 3 + 2] = z;
      seed[i * 3] = Math.random(); seed[i * 3 + 1] = Math.random(); seed[i * 3 + 2] = Math.random();
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 3));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 100);
    this._dustUniforms = Object.assign({}, this._sharedUniforms, { uPixelScale: { value: 1 } });
    this._dustMat = new THREE.ShaderMaterial({
      uniforms: this._dustUniforms, vertexShader: dustVertexShader(this._layout.strobes, this._layout.lyres), fragmentShader: dustFragmentShader,
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending
    });
    this._dustGeo = geo;
    this._dust = new THREE.Points(geo, this._dustMat);
    this._dust.frustumCulled = false;
    this._dust.visible = false;
    this._hazeScene.add(this._dust);
  }

  // --- the rhythm ---------------------------------------------------------------

  // One strobe on the beat, drawn by the current pattern.
  _strobeForBeat() {
    const n = this._strobePos.length;
    const k = this._strobeTurn++;
    switch (STROBE_PATTERNS[this._strobePattern % STROBE_PATTERNS.length]) {
      case 'tournant': return k % n;
      case 'alterne': return (k % 2 === 0) ? (2 * ((k >> 1) % Math.ceil(n / 2))) % n : (2 * ((k >> 1) % Math.ceil(n / 2)) + 1) % n;
      case 'croise': return ((k * 3) + ((k / n) | 0)) % n;
      default: return (Math.random() * n) | 0;
    }
  }

  _fireStrobe(i, dur) {
    if (i < 0 || i >= this._strobeUntil.length) return;
    this._strobeUntil[i] = Math.max(this._strobeUntil[i], this._time + dur);
  }

  // COMFORT: three full-frame flashes a second at most; a bar beyond that is
  // skipped, never shortened.
  _fireAll(dur) {
    while (this._fullFires.length && this._time - this._fullFires[0] > 1.0) this._fullFires.shift();
    if (this._fullFires.length >= FULL_PER_SEC) { this._skippedBars++; return false; }
    this._fullFires.push(this._time);
    for (let i = 0; i < this._strobeUntil.length; i++) this._fireStrobe(i, dur);
    return true;
  }

  // The lyres' target for this pattern and this step. Directions in camera
  // space (+y up, +z into the hall); the "inward" axis points across the
  // hall from the lyre's own side.
  _aim(i, out) {
    const n = this._lyrePos.length;
    const side = LYRE_SIDE[i];
    const inward = -side;
    const row = (i >> 1);
    const s = this._step;
    switch (this._pattern) {
      case PAT_FAN: {
        const sweep = Math.sin((s * 0.5 + row * 0.7) * Math.PI / 2);
        out.set(inward * (0.45 + 0.35 * sweep), -0.55, 0.55 + 0.45 * (row / 3) + 0.25 * sweep);
        break;
      }
      case PAT_WAVE: {
        const ph = s * 0.6 + row * 1.1;
        out.set(inward * 0.35, -0.35 - 0.45 * (0.5 + 0.5 * Math.sin(ph)), 0.75 + 0.3 * Math.cos(ph));
        break;
      }
      case PAT_CROSS: {
        const flip = ((s + row) & 1) ? 1 : -1;
        out.set(inward * 0.85, -0.45, 0.35 * flip);
        break;
      }
      case PAT_RAIN: {
        out.set(inward * 0.08 + 0.1 * Math.sin(s * 1.3 + i), -1.0, 0.12 * Math.cos(s * 0.9 + i * 0.6));
        break;
      }
      case PAT_CHASE: {
        const lit = (s % n) === i;
        this._lyreLevel[i] = lit ? 1 : 0.15;
        const floor = this._floorPoint((s * 0.37) % 1);
        out.copy(floor).sub(this._lyrePos[i]);
        break;
      }
      default: {   // CONVERGE: everything on the platform
        out.copy(this._boothPos || new THREE.Vector3(0, -0.3, BOOTH_Z)).sub(this._lyrePos[i]);
        out.y += 0.15;
      }
    }
    if (this._pattern !== PAT_CHASE) this._lyreLevel[i] = 1;
    out.normalize();
  }

  _floorPoint(t) {
    const hc = 1 - this._meta.vanish.y;
    return new THREE.Vector3((t * 2 - 1) * 0.9, -hc, 1.3 + 2.5 * Math.abs(Math.sin(t * 7.1)));
  }

  _stepLyres() {
    this._step++;
    for (let i = 0; i < this._lyrePos.length; i++) {
      this._lyreFrom[i].copy(this._lyreDir[i]);
      this._aim(i, this._lyreTo[i]);
    }
    this._sinceStep = 0;
  }

  _setPattern(p) {
    this._pattern = p;
    this._stepLyres();
  }

  // --- the frame -------------------------------------------------------------------

  update(deltaTime, profile) {
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;

    const director = profile.director || {};
    const bands = director.bands || null;
    const has = !!(bands && director.hasReliableGrid);
    const kb = 1 - Math.exp(-dt / 0.25);
    this._lowSmooth += ((has ? clamp01(bands.low) : 0.5 + 0.35 * Math.sin(this._time * 1.9)) - this._lowSmooth) * kb;
    this._highSmooth += ((has ? clamp01(bands.high) : 0.5) - this._highSmooth) * kb;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || FALLBACK_BPM;
    const beatS = 60 / Math.max(60, bpm);

    // --- the grid, or a beat of our own -------------------------------------
    let newBeat = false, newBar = false, newPhrase = false;
    if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase - 0.5) newBeat = true;
      if (this._lastBeatPhase == null) newBeat = true;
      this._lastBeatPhase = profile.beatPhase;
      if (profile.barPhase != null) {
        if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase - 0.5) newBar = true;
        this._lastBarPhase = profile.barPhase;
      } else if (newBeat && (this._beatsSeen % 4) === 0) newBar = true;
      if (profile.phraseIndex != null && this._lastPhrase != null && profile.phraseIndex !== this._lastPhrase) newPhrase = true;
      if (profile.phraseIndex != null) this._lastPhrase = profile.phraseIndex;
    } else {
      this._ownBeat += dt;
      if (this._ownBeat >= beatS) { this._ownBeat -= beatS; newBeat = true; }
      if (newBeat && (this._beatsSeen % 4) === 0) newBar = true;
      if (newBeat && (this._beatsSeen % 16) === 0 && this._beatsSeen > 0) newPhrase = true;
    }
    if (newBeat) { this._beatsSeen++; this._beatPulse = 1; }
    this._beatPulse = Math.max(0, (this._beatPulse || 0) - dt * BEAM_PULSE_DECAY * (this._beatPulse || 0) - dt * 0.5);
    const pulse = BEAM_PULSE_FLOOR + (1 - BEAM_PULSE_FLOOR) * (this._beatPulse || 0);

    // --- palette: beams and the sheet in the palette at saturation 0.85, the
    // platform in the first dominant colour ------------------------------------
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const pal = profile.artworkPalette;
      const vivid = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0x3060ff, 0xff2060, 0x20ff80];
      const sat = function (hex) {
        const c = new THREE.Color(hex); c.getHSL(_hsl);
        return c.setHSL(_hsl.h, BEAM_SAT, 0.55);
      };
      this._beamColors = [sat(vivid[0]), sat(vivid[1 % vivid.length])];
      this._sheetColor = sat(vivid[2 % vivid.length]);
      const dom = pal && pal.dominantColors && pal.dominantColors.length ? pal.dominantColors[0] : vivid[0];
      this._boothColor = new THREE.Color(dom);
      this._boothColor.getHSL(_hsl);
      this._boothColor.setHSL(_hsl.h, Math.max(0.5, _hsl.s), 0.5);
    }

    // --- the drop: black for a beat, then everything converges -------------------
    const ev = director.nextEvent;
    const dropNow = (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0 && this._time - this._lastDropAt > 4.0)
      || (this._forceDrop && this._time - this._lastDropAt > 8.0);
    if (dropNow) {
      this._lastDropAt = this._time;
      this._dropAt = this._time;
      this._dropBlackUntil = this._time + DROP_BLACK_BEATS * beatS;
      this._dropUntil = this._dropBlackUntil + DROP_BEATS * beatS;
      this._dropFired = false;
      for (let i = 0; i < this._strobeUntil.length; i++) this._strobeUntil[i] = -1;
    }
    const inBlack = this._time < (this._dropBlackUntil || -1);
    const inDrop = !inBlack && this._time < (this._dropUntil || -1);
    if (inDrop && !this._dropFired) {
      this._dropFired = true;
      this._fireAll(0.090);
      this._setPattern(PAT_CONVERGE);
      this._dropSheet = true;
    }
    if (!inDrop && this._dropSheet) {
      this._dropSheet = false;
      this._setPattern(PATTERN_CYCLE[this._patternIdx % PATTERN_CYCLE.length]);
    }

    // --- events: one per beat -----------------------------------------------------
    if (newPhrase && !inDrop) {
      this._patternIdx++;
      this._setPattern(this._forcedPattern != null ? this._forcedPattern : PATTERN_CYCLE[this._patternIdx % PATTERN_CYCLE.length]);
      this._sheetIdx++;
      this._sheetMode = this._forcedSheet != null ? this._forcedSheet : SHEET_MODES[this._sheetIdx % SHEET_MODES.length];
      this._strobePattern++;
    }
    let events = 0;
    if (newBeat && !inBlack) {
      const dur = BEAT_MS[1] - (BEAT_MS[1] - BEAT_MS[0]) * clamp01(this._highSmooth);
      if (newBar) {
        if (this._fireAll(BAR_MS)) events++;
        else { this._fireStrobe(this._strobeForBeat(), dur); events++; }
      } else {
        this._fireStrobe(this._strobeForBeat(), dur);
        events++;
      }
      if (this._lyrePos.length) { this._stepLyres(); events++; }
      this._eventsPerBeat.push(events);
      if (this._eventsPerBeat.length > 64) this._eventsPerBeat.shift();
    }
    if (this._forceStrobe === 'all') for (let i = 0; i < this._strobeUntil.length; i++) this._strobeUntil[i] = this._time + 1;
    else if (this._forceStrobe != null && Number.isFinite(+this._forceStrobe)) this._strobeUntil[(+this._forceStrobe) % this._strobeUntil.length] = this._time + 1;

    // --- the strobes' thermal step, and the luminance governor --------------------
    const heat = Math.min(1, HEAT_RATE * dt), cool = Math.min(1, COOL_RATE * dt);
    let tempSum = 0;
    for (let i = 0; i < this._strobeTemp.length; i++) {
      const on = this._time < this._strobeUntil[i] && !inBlack;
      this._strobeTemp[i] += on ? (1 - this._strobeTemp[i]) * heat : -this._strobeTemp[i] * cool;
      tempSum += this._strobeTemp[i];
    }
    this._governLuminance(tempSum / Math.max(1, this._strobeTemp.length), dt);
    const sg = this._gain * (this._forceNone ? 0 : 1);
    const A = this._aspectPhoto, vx = this._meta.vanish.x, vy = this._meta.vanish.y;
    for (let i = 0; i < this._strobeTemp.length; i++) {
      const p = this._strobePos[i];
      this._sharedUniforms.uStrobe.value[i].set(p.x, p.y, p.z, this._strobeTemp[i] * sg);
      this._sharedUniforms.uStrobeImg.value[i].set((vx + p.x / (A * p.z)) * A, vy - p.y / p.z);
    }

    // --- the lyres: ease to the target over MOVE_MS, then hold ---------------------
    this._sinceStep += dt;
    const e = clamp01(this._sinceStep * 1000 / MOVE_MS);
    const ease = e * e * (3 - 2 * e);
    const lyreOn = !inBlack && !this._forceNone;
    for (let i = 0; i < this._lyrePos.length; i++) {
      this._lyreDir[i].copy(this._lyreFrom[i]).lerp(this._lyreTo[i], ease).normalize();
      const level = lyreOn ? this._lyreLevel[i] * BEAM_LIGHT * pulse : 0;
      const c = this._beamColors[i % 2];
      const o = this._lyrePos[i];
      this._sharedUniforms.uBeamO.value[i].set(o.x, o.y, o.z, level);
      this._sharedUniforms.uBeamD.value[i].set(this._lyreDir[i].x, this._lyreDir[i].y, this._lyreDir[i].z, BEAM_TAN);
      this._sharedUniforms.uBeamC.value[i].set(c.r, c.g, c.b);
      const cu = this._cones[i].uniforms;
      cu.uColor.value.set(c.r, c.g, c.b);
      cu.uLevel.value = lyreOn ? this._lyreLevel[i] * pulse : 0;
    }

    // --- the sheet --------------------------------------------------------------
    {
      let pitch, fan = 0, h = SHEET_H;
      const bar = profile.barPhase != null ? profile.barPhase : (this._beatsSeen % 4 + this._ownBeat / beatS) / 4;
      if (this._dropSheet) { pitch = 0; h = SHEET_DROP_H - BOOTH_H; }   // the FLOOR, not the deck
      else if (this._sheetMode === SHEET_MODE_STATIC) pitch = SHEET_STATIC;
      else if (this._sheetMode === SHEET_MODE_FAN) { pitch = 0.02 + 0.03 * Math.sin(this._time * 0.7); fan = 1; }
      else pitch = lerp(SHEET_SWEEP[0], SHEET_SWEEP[1], 0.5 + 0.5 * Math.sin(bar * Math.PI * 2 * 0.5 + this._time * 0.15));
      this._sheetPitch += (pitch - this._sheetPitch) * Math.min(1, dt * 12);
      const sp = this._sheetPos || new THREE.Vector3(0, 0, BOOTH_Z);
      const hc = 1 - this._meta.vanish.y;
      const level = (inBlack || this._forceNone || !this._layout.sheet) ? 0 : 1;
      this._sharedUniforms.uSheet0.value.set(sp.x, (BOOTH_H + h) - hc, sp.z, level);
      this._sharedUniforms.uSheet1.value.set(this._sheetPitch, SHEET_THICKNESS, fan, SHEET_SPREAD);
      this._sharedUniforms.uSheetC.value.set(this._sheetColor.r, this._sheetColor.g, this._sheetColor.b);
    }

    // --- the platform: always there, on the bass ---------------------------------
    {
      const target = (inBlack ? 0 : 1) * (1 - BOOTH_LOW * 0.5 + BOOTH_LOW * this._lowSmooth);
      this._boothLevel += (target - this._boothLevel) * Math.min(1, dt * 8);
      const bp = this._boothPos || new THREE.Vector3(0, -0.2, BOOTH_Z);
      this._sharedUniforms.uBooth.value.set(bp.x, bp.y, bp.z, this._boothLevel);
      this._sharedUniforms.uBoothImg.value.set((vx + bp.x / (A * bp.z)) * A, vy - bp.y / bp.z);
      this._sharedUniforms.uBoothC.value.set(this._boothColor.r, this._boothColor.g, this._boothColor.b);
    }

    this._sharedUniforms.uTime.value = this._time;
  }

  // COMFORT: the one-second mean of the predicted frame luminance, from the
  // strobes' mean temperature through a transfer measured in the harness
  // (LUM_DARK with nothing lit, LUM_FULL with every strobe at 1). Over the
  // limit the strobes' gain comes down at once and goes back up slowly.
  _governLuminance(meanTemp, dt) {
    const lum = LUM_DARK + (LUM_FULL - LUM_DARK) * Math.pow(clamp01(meanTemp * this._gain), LUM_POW);
    this._lumSamples.push([this._time, lum]);
    while (this._lumSamples.length && this._time - this._lumSamples[0][0] > LUM_WINDOW) this._lumSamples.shift();
    let sum = 0;
    for (let i = 0; i < this._lumSamples.length; i++) sum += this._lumSamples[i][1];
    this._lumMean = sum / Math.max(1, this._lumSamples.length);
    const over = this._lumMean / LUM_LIMIT;
    const want = over > 1 ? Math.max(0.25, 1 / over) : 1;
    if (want < this._gain) this._gain = want;
    else this._gain = Math.min(1, this._gain + dt * GAIN_RELEASE);
  }

  // The verification's probe.
  get probe() {
    return {
      ready: this._ready, tier: this._tier,
      strobes: Array.from(this._strobeTemp).map((t) => +t.toFixed(3)),
      strobeScreen: this._strobePos.map((p) => this._screenOf(p)),
      lyres: this._lyreDir.map((d, i) => ({ dir: [+d.x.toFixed(2), +d.y.toFixed(2), +d.z.toFixed(2)], level: this._lyreLevel[i] })),
      pattern: this._pattern, sheetMode: this._sheetMode, sheetPitch: +this._sheetPitch.toFixed(4),
      fullFiresLastSecond: this._fullFires.filter((t) => this._time - t <= 1).length, skippedBars: this._skippedBars,
      lumMean: +this._lumMean.toFixed(3), gain: +this._gain.toFixed(3),
      eventsPerBeat: this._eventsPerBeat.slice(), beatsSeen: this._beatsSeen,
      meta: this._meta, fit: [this._sharedUniforms.uFit.value.x, this._sharedUniforms.uFit.value.y]
    };
  }
  _screenOf(p) {
    const vx = this._meta.vanish.x, vy = this._meta.vanish.y;
    return [+(vx + p.x / (this._aspectPhoto * p.z)).toFixed(3), +(vy - p.y / p.z).toFixed(3)];
  }

  dispose() {
    this._scene.remove(this._photo);
    this._photo.geometry.dispose();
    this._photo.material.dispose();
    this._lightQuad.geometry.dispose();
    this._lightQuad.material.dispose();
    this._rtLight.dispose();
    this._rtHaze.dispose();
    for (const c of this._cones) { this._hazeScene.remove(c.mesh); c.mat.dispose(); }
    if (this._coneGeo) this._coneGeo.dispose();
    if (this._dust) { this._hazeScene.remove(this._dust); this._dustGeo.dispose(); this._dustMat.dispose(); }
    if (this._photoTex) this._photoTex.dispose();
    if (this._depthTex) this._depthTex.dispose();
    if (this._normalTex) this._normalTex.dispose();
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _hsl = { h: 0, s: 0, l: 0 };
